import type { CostTier, ModelBenchmark, OrgModel } from '../../store/api/orgModelsApi';

export const MAX_COMPARE = 3;
export const PAYMENT_METHOD_REQUIRED = 'payment_method_required';
export const MODEL_PLAYGROUND_AGENT = 'model-playground';

export const COST_TIER_LABELS: Record<CostTier, string> = {
  1: 'Lowest',
  2: 'Low',
  3: 'Medium',
  4: 'Highest',
};

/** Relative cost only — never a per-token rate. */
export function costTierLabel(tier?: CostTier | null): string {
  return tier ? COST_TIER_LABELS[tier] : 'Not priced yet';
}

export function costTierDots(tier?: CostTier | null): string {
  if (!tier) return '';
  return '●'.repeat(tier) + '○'.repeat(4 - tier);
}

export function formatTokens(value?: number | null): string {
  if (!value) return '—';
  if (value >= 1_000_000) return `${+(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
  return String(value);
}

export function benchmarkByLabel(
  model: Pick<OrgModel, 'benchmarks'>,
  label: string,
): ModelBenchmark | undefined {
  return (model.benchmarks || []).find((b) => b.label === label);
}

/** Catalog "Top score": Reasoning when published, else Coding — always labelled. */
export function topScore(model: Pick<OrgModel, 'benchmarks'>): ModelBenchmark | null {
  return (
    benchmarkByLabel(model, 'Reasoning') || benchmarkByLabel(model, 'Coding') || null
  );
}

export function parseModelIds(value: string | null): string[] {
  if (!value) return [];
  const ids: string[] = [];
  for (const raw of value.split(',')) {
    const id = raw.trim();
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids.slice(0, MAX_COMPARE);
}

export function canManageModels(role: string): boolean {
  return role === 'owner' || role === 'admin';
}

export function canTryModels(role: string): boolean {
  return role !== 'viewer';
}

export function modelEnableBlocked(model: OrgModel): boolean {
  return !model.enabled && model.enableBlockedReason === PAYMENT_METHOD_REQUIRED;
}

export function createAgentPath(productId: string, instructions?: string): string {
  const params = new URLSearchParams({ model: productId });
  const trimmed = instructions?.trim();
  if (trimmed) params.set('instructions', trimmed);
  return `/agents/new?${params.toString()}`;
}

export function mutationErrorMessage(error: unknown): string {
  const fallback = 'Something went wrong. Please try again.';
  const err = error as { status?: string | number; data?: unknown } | undefined;
  if (err?.status === 'FETCH_ERROR') {
    return 'Could not reach the API. Check your connection and try again.';
  }
  const data = err?.data as Record<string, unknown> | undefined;
  if (data && typeof data.message === 'string') return data.message;
  return fallback;
}
